import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQPageSchema,
} from "@/components/SEO";

const otherAreas = [
  { title: "Health", href: "/our-work/health", icon: "ri-heart-pulse-line", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { title: "Education", href: "/our-work/education", icon: "ri-book-open-line", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { title: "Protection", href: "/our-work/protection", icon: "ri-shield-check-line", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

export default function EconomicSecurityPage() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useSEOMeta({
    title: "Economic Security - BHODEV Cameroon | Women Empowerment, Livelihoods & Environment",
    description:
      "BHODEV promotes livelihoods and economic inclusion for IDPs in Cameroon's North-West and South-West regions. Micro-business support, women empowerment, sustainable environment, and social protection advocacy.",
    keywords:
      "BHODEV economic security, women empowerment Cameroon, IDP livelihood Cameroon, micro business Cameroon, sustainable environment Cameroon, social protection advocacy, Cameroon women entrepreneurs, economic inclusion",
    canonicalUrl: "https://bhodevcameroon.org/our-work/economic-security",
    ogImage:
      "https://readdy.ai/api/search-image?query=women%20entrepreneurs%20in%20Cameroon%20working%20on%20micro%20business%20activities%20market%20stalls%20community%20empowerment%20vibrant%20colors%20natural%20light%20documentary%20style%20wide%20landscape&width=1200&height=630&seq=economic-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
        { name: "Economic Security", url: "https://bhodevcameroon.org/our-work/economic-security" },
      ]),
      ...generateWebPageSchema(
        "Economic Security - BHODEV Cameroon",
        "BHODEV promotes livelihoods and economic inclusion for IDPs in Cameroon through micro-business support, women empowerment, sustainable environment, and social protection advocacy.",
        "https://bhodevcameroon.org/our-work/economic-security"
      ),
      ...generateFAQPageSchema([
        {
          question: "How does BHODEV support women's economic empowerment?",
          answer:
            "BHODEV improves household incomes of vulnerable women through establishing micro-businesses, sustainable micro-projects and micro-credit operations, empowering more than 850 women across Cameroon.",
        },
        {
          question: "What is BHODEV's approach to environmental sustainability?",
          answer:
            "BHODEV joins forces with UNEP implementing four approaches: nature conservation and education, natural resource management, advocacy for carbon-neutral world, water and sanitation, and community environmental rehabilitation.",
        },
      ]),
    },
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("sector", "Economic Security")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching economic security projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fallbackProjects = [
    {
      title: "Women Micro-Business Initiative",
      category: "Women Empowerment",
      description: "Improving household incomes of vulnerable women through establishing micro-businesses, sustainable micro-projects and micro-credit operations.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=women%20micro%20business%20initiative%20in%20Cameroon%20female%20entrepreneurs%20running%20small%20businesses%20market%20stalls%20economic%20empowerment%20vibrant%20community%20setting&width=400&height=260&seq=ecp1&orientation=landscape",
    },
    {
      title: "IDP Relocation Support Programme",
      category: "Livelihood",
      description: "Helping vulnerable people who were forced to flee their homes to new communities by promoting their economic inclusion and advocating for their right to work.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=IDP%20relocation%20support%20in%20Cameroon%20displaced%20families%20receiving%20livelihood%20assistance%20community%20integration%20humanitarian%20workers%20warm%20natural%20light&width=400&height=260&seq=ecp2&orientation=landscape",
    },
    {
      title: "Sustainable Environment Programme",
      category: "Environment",
      description: "Nature conservation, natural resource management, advocacy for carbon-neutral world, water and sanitation, and community development and environmental rehabilitation.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=environmental%20conservation%20programme%20in%20Cameroon%20community%20members%20planting%20trees%20nature%20conservation%20green%20landscape%20sustainable%20development&width=400&height=260&seq=ecp3&orientation=landscape",
    },
    {
      title: "Social Protection Advocacy",
      category: "Advocacy",
      description: "Collaborating with partners, development and humanitarian organizations, the private sector and civil society to enhance economic outcomes for IDPs and host communities.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=social%20protection%20advocacy%20meeting%20in%20Cameroon%20NGO%20workers%20and%20community%20leaders%20discussing%20economic%20rights%20conference%20room%20professional%20setting&width=400&height=260&seq=ecp4&orientation=landscape",
    },
  ];

  const displayProjects = projects.length > 0 ? projects : fallbackProjects;

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[460px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-water-hygiene-and-sanitation-mile-16%2FIMG-20230504-WA0066.jpg?alt=media&token=c1894e5b-f286-423f-ac50-4c54636d8865"
            alt="BHODEV Economic Security Work in Cameroon - Women Empowerment"
            title="BHODEV Economic Security Work in Cameroon - Women Empowerment"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto w-full">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/our-work" className="hover:text-white transition-colors duration-200">Our Work</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Economic Security</span>
          </nav>
          <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">Core Sector</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Economic Security</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            BHODEV helps save lives and builds a better future for the millions of IDPs forced to flee their homes in the North-West and South-West Regions.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-teal-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="w-full lg:w-3/5">
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3 block">Core Sectors — Economic Security</span>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-5">Our Approach to Economic Security</h2>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                BHODEV helps to save lives and builds better future for the millions of IDPs forced to flee their homes in the North-West and South-West Regions due to the socio-political crisis in Cameroon.
              </p>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed">
                Living in dignity and in peace is through making use of the opportunity to work and earn a living. We strive to promote livelihoods and economic inclusion for victims of socio-political crisis in the NWSW and advocate for their right to work and support them in becoming self-dependent. <strong>One of the global challenges deals with the empowerment of women that is achieving women's equal access to and control over economic resources.</strong>
              </p>
            </div>
            <div className="w-full lg:w-2/5 grid grid-cols-2 gap-3">
              {[
                { icon: "ri-women-line", label: "Women Empowered", value: "850+" },
                { icon: "ri-store-2-line", label: "Micro-Businesses", value: "200+" },
                { icon: "ri-leaf-line", label: "Env. Approaches", value: "4" },
                { icon: "ri-global-line", label: "SDG Aligned", value: "SDG 1,8" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 border border-teal-100 text-center">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-600 mx-auto mb-2">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="text-lg font-bold text-stone-800">{stat.value}</p>
                  <p className="text-xs text-stone-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Improving Livelihood</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                The BHODEV family believes that, after fleeing from a natural disaster or war environment, one of the most effective ways people can revamp their lives with dignity and in peace is through making use of the opportunity to work and earn a living. We strive to promote livelihoods and economic inclusion for victims of socio-political crisis in the NWSW and advocate for their right to work and support them in becoming self-dependent. One of the global challenges deals with the empowerment of women that is achieving women's equal access to and control over economic resources.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-5">
                Revamping a person's life entails more than just having a roof over his/her head and a safe place to sleep, but rather having full control of your future. Achieving this entails a decent work that can provide for their families needs, making use of their skills and talents, become more resilient, consistent and be empowered to shape their future by themselves.
              </p>

              <h4 className="text-sm font-bold text-stone-800 mb-3 uppercase tracking-wide">How BHODEV Helps</h4>
              <p className="text-stone-600 text-sm leading-relaxed">
                <strong>Our main goal is to improve the household incomes of vulnerable women</strong> through establishing micro-businesses, sustainable micro-projects and micro-credit operations such that our beneficiaries are self-reliant. We also help the vulnerable people who were forced to flee their homes to new communities by promoting their economic inclusion, advocating for their right to work and supporting their inclusion in government social protection systems by collaborating with a wide range of partners, development and humanitarian organizations, the private sector and civil society to enhance economic outcomes for both the IDPs and host communities in the NWSW.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Sustainable Environment</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                The environment is fundamental to humanitarian actions as environmental issues are often underlying and contributing factors to humanitarian crisis.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Humanitarian crises can also have negative effects on the environment and exacerbate the risks and vulnerability of the people if managed inadequately or addressed too late. Unless greenhouse gas emissions drop drastically, global warming could pass 2.9°C in our world today which will lead to catastrophic consequences for life on the planet. <strong>BHODEV join forces with United Nations Environment Programme (UNEP)</strong> in implementing the four prolonged approaches to addressing climate crisis and reducing the level of greenhouse gas emissions which is in line with the Paris Agreement.
              </p>

              <h4 className="text-sm font-bold text-stone-700 mb-3">These approaches include:</h4>
              <ul className="flex flex-col gap-2 mb-4">
                {[
                  "Nature Conservation, Education and Sensitization.",
                  "Natural Resource Management and Conservation.",
                  "Advocacy and Policy works on carbon-neutral world by empowering communities adapt to changing climatic conditions.",
                  "Water and Sanitation.",
                  "Community Development and Environmental Rehabilitation.",
                  "Consultancy and Research.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-stone-600">
                    <div className="w-4 h-4 flex items-center justify-center text-teal-600 shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">Organization Projects under Economic Security</h2>
          <p className="text-stone-500 text-sm mb-10">Programmes and initiatives driving economic outcomes in Cameroon</p>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-teal-600 text-2xl"></i>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProjects.map((project) => (
                <Link
                  key={project.id || project.title}
                  to={project.id ? `/projects/${project.id}` : "#"}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-teal-200 transition-all duration-300 group cursor-pointer block"
                >
                  <div className="w-full h-[160px]">
                    <img
                      src={project.image_url || project.image}
                      alt={project.title}
                      title={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                      {project.status || project.category}
                    </span>
                    <h3 className="text-sm font-bold text-stone-800 mb-2 leading-snug">{project.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-3">
                      {project.description || project.excerpt || ""}
                    </p>
                    <p className="text-xs text-stone-400 mt-3 font-medium">{project.start_date || project.year}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Core Areas */}
      <section className="py-12 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-stone-800 mb-6">Explore Other Core Areas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherAreas.map((area) => (
              <Link
                key={area.title}
                to={area.href}
                className={`flex items-center gap-3 px-5 py-4 rounded-xl border ${area.color} hover:scale-105 transition-all duration-200 cursor-pointer`}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${area.icon} text-xl`}></i>
                </div>
                <span className="font-semibold text-sm">{area.title}</span>
                <i className="ri-arrow-right-line ml-auto text-sm"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}